import { NgClass, CommonModule, DatePipe } from '@angular/common';
import { Component, inject, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ICONS_RAW } from '../../../../../shared/constants/icons-raw';
import { SIDEBAR_AREA } from '../../../../../shared/constants/sidebar-area';
import { STORAGE_KEYS } from '../../../../../shared/constants/storage-keys';
import { DecisionLevel } from '../../../../../shared/enums/decision-level';
import { EditComponentType } from '../../../../../shared/enums/edit-component-type';
import { SwPartNumberState } from '../../../../../shared/enums/sw-part-number-state';
import { SidebarConfiguration } from '../../../../../shared/internal/sidebar/sidebar-configuration';
import { AggreatedLanguageTranslationState } from '../../../../../shared/models/dtc/aggreated-language-translation-state';
import { LanguageTranslationState } from '../../../../../shared/models/dtc/language-translation-state';
import { Decision } from '../../../../../shared/models/internal/decision';
import { DetailsViewConfiguration } from '../../../../../shared/models/internal/details/details-view-configuration';
import { Textarea } from '../../../../../shared/models/internal/details/textarea';
import { Paragraph } from '../../../../../shared/models/internal/popover/paragraph';
import { NwNumberErrorMemory } from '../../../../../shared/models/nw-number/nw-number-error-memory';
import { CreateMajorBranch } from '../../../../../shared/models/sw-part-number/create-major-branch';
import { CreateSnapshot } from '../../../../../shared/models/sw-part-number/create-snapshot';
import { GroupedSwPartNumber } from '../../../../../shared/models/sw-part-number/grouped-sw-part-number';
import { SwPartNumber } from '../../../../../shared/models/sw-part-number/sw-part-number';
import { SwPartNumberQuery } from '../../../../../shared/models/sw-part-number/sw-part-number-query';
import { SwPartNumberUpdateConfirmation } from '../../../../../shared/models/sw-part-number/sw-part-number-update-confirmation';
import { CommonUtils, errorToString } from '../../../../common/common-utils';
import { EnabledByPolicyDirective } from '../../../../directives/enabled-by-policy.directive';
import { NwNumberErrorMemoryApiService } from '../../../../services/api/nw-number-error-memory-api.service';
import { SwPartNumberApiService } from '../../../../services/api/sw-part-number-api.service';
import { CrudOverviewBaseComponent } from '../../../base/crud-overview-base.component';
import { HelpPopoverComponent } from '../../../common/popover/help-popover/help-popover.component';
import { SidebarComponent } from '../../../common/sidebar/sidebar.component';
import { RestoreIconComponent } from '../../../icons/restore-icon/restore-icon.component';
import { SwPartNumberAccordionComponent } from '../../../lists/sw-part-number/sw-part-number-accordion/sw-part-number-accordion.component';
import { DecisionModalComponent } from '../../../modals/decision-modal/decision-modal.component';
import { ServiceApiService } from '../../../../services/api/service-api.service';
import { ImportReport } from '../../../../../shared/models/report/import-report';
import { ImportType } from '../../../lists/sw-part-number/sw-part-number-accordion/import-type';
import { ApprovalStatus } from '../../../../../shared/models/internal/details/approval-status';
import { IfPolicySatisfiedDirective } from '../../../../directives/if-policy-satisfied.directive';
import { ImportCreateIconComponent } from '../../../icons/import-create-icon/import-create-icon.component';
import { AgentActionDirective } from '../../../../directives/agent-action.directive';
import { TranslationService } from '../../../../services/translation.service';
import { TrReviewStatistics } from '../../../../../shared/models/internal/details/tr-review-statistics';
import { HotToastService } from '@ngxpert/hot-toast';

@Component({
  selector: 'app-sw-part-number-overview',
  imports: [
    SwPartNumberAccordionComponent,
    SidebarComponent,
    NgClass,
    RestoreIconComponent,
    RouterModule,
    DecisionModalComponent,
    EnabledByPolicyDirective,
    HelpPopoverComponent,
    CommonModule,
    IfPolicySatisfiedDirective,
    ImportCreateIconComponent,
    AgentActionDirective,
  ],
  templateUrl: './sw-part-number-overview.component.html',
  styleUrl: './sw-part-number-overview.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [DatePipe],
})
export class SwPartNumberOverviewComponent extends CrudOverviewBaseComponent<SwPartNumber> implements OnInit {
  @ViewChild('createNewBranchModal')
  public createNewwBranchModal!: DecisionModalComponent;
  @ViewChild('createSnapshotModal')
  public createSnapshotModal!: DecisionModalComponent;
  @ViewChild('updateSnapshotModal')
  public updateSnapshotModal!: DecisionModalComponent;
  @ViewChild('deactivateSwPartNumberModal')
  public deactivateSwPartNumberModal!: DecisionModalComponent;
  @ViewChild('releaseSnapshotModal')
  public releaseSnapshotModal!: DecisionModalComponent;

  public groupedSwPartNumber: GroupedSwPartNumber[] = [];
  public swPartNumberDetailConfiguration?: DetailsViewConfiguration;
  public swPartNumberToDecide?: SwPartNumber;
  public readonly decisionLevel = DecisionLevel.Warning;
  public readonly editComponentType = EditComponentType;
  public aggregatedLanguageTranslationStatesDocumentation: LanguageTranslationState[] = [];
  public aggregatedLanguageTranslationStatesTerminal: LanguageTranslationState[] = [];
  public paragraphs: Paragraph[] = [];
  public preselectedSwPartNumber: string | null = null;
  public report?: { swPartNumberId: string; importReport: ImportReport };
  public sidebarConfiguration: SidebarConfiguration = new SidebarConfiguration();
  public nwNumberErrorMemory?: NwNumberErrorMemory;

  private readonly swPartNumberApiService = inject(SwPartNumberApiService);
  private readonly serviceApiService = inject(ServiceApiService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly datePipe = inject(DatePipe);
  private readonly nwNumberErrorMemoryApi = inject(NwNumberErrorMemoryApiService);
  private toast = inject(HotToastService);

  private snapshotSourceIdToDecide?: string;
  private snapshotMinorVersionToDecide?: string;

  constructor() {
    super(STORAGE_KEYS.UNSAVED_SW_PART_NUMBER);
  }

  public override ngOnInit(): void {
    super.ngOnInit();
    this.buildHelpParagraphs();

    this.fetchGroupedSwPartNumbers();
  }

  public getItemsTotalCount(): number {
    return this.groupedSwPartNumber.reduce((total, group) => total + group.swPartNumbers!.length, 0);
  }

  public handleCreateSnapshot(data: { sourceSwPartNumber: SwPartNumber; minorVersion: number }): void {
    this.swPartNumberToDecide = data.sourceSwPartNumber;
    this.snapshotMinorVersionToDecide = data.minorVersion.toString().padStart(3, '0');

    this.createSnapshotModal.openModal();
  }

  public handleCreateBranch(swPartNumber: SwPartNumber): void {
    this.swPartNumberToDecide = swPartNumber;
    this.createNewwBranchModal.openModal();
  }

  public handleReleaseSnapshot(swPartNumber: SwPartNumber): void {
    this.swPartNumberToDecide = swPartNumber;
    this.releaseSnapshotModal.openModal();
  }

  public handleNewBranchDecision(decision: Decision): void {
    if (decision.result && this.swPartNumberToDecide) {
      this.createNewBranch(this.swPartNumberToDecide);
      this.swPartNumberToDecide = undefined;
    }
  }

  public handleCreateSnapshotDecision(decision: Decision): void {
    if (decision.result && this.swPartNumberToDecide && this.snapshotMinorVersionToDecide) {
      this.createSnapshot(
        this.swPartNumberToDecide.id!,
        `${this.swPartNumberToDecide.nwNumber}-${this.swPartNumberToDecide.majorRelease}.${this.snapshotMinorVersionToDecide}`,
        this.snapshotMinorVersionToDecide
      );
    }
  }

  public handleForceUpdateSnapshotDecision(decision: Decision): void {
    if (decision.result && this.snapshotSourceIdToDecide && this.snapshotMinorVersionToDecide) {
      this.executeCreateSnapshot(this.snapshotSourceIdToDecide, this.snapshotMinorVersionToDecide);
      this.snapshotSourceIdToDecide = undefined;
      this.snapshotMinorVersionToDecide = undefined;
    }
  }

  public handleReleaseSnapshotModelDecision(decision: Decision): void {
    if (decision.result && this.swPartNumberToDecide?.id) {
      this.swPartNumberApiService.releaseSnapshot(this.swPartNumberToDecide.id).subscribe({
        next: (createdSwPartNumber) => {
          this.reloadSwPartNumberList(createdSwPartNumber);
        },
        error: (error) => {
          console.error(errorToString(error));
        },
      });
    }
    this.swPartNumberToDecide = undefined;
  }

  public handleSoftDelete(swPartNumber: SwPartNumber): void {
    this.swPartNumberToDecide = swPartNumber;
    this.deactivateSwPartNumberModal.openModal();
  }

  public handleUpdateSnapshot(swPartNumber: SwPartNumber): void {
    if (!swPartNumber.parentId) {
      return;
    }
    this.swPartNumberToDecide = swPartNumber;
    this.createSnapshot(swPartNumber.parentId!, swPartNumber.number!, swPartNumber.minorRelease!);
  }

  public handleSoftDeleteDecision(decision: Decision): void {
    if (decision.result && this.swPartNumberToDecide?.id) {
      this.swPartNumberApiService.deleteSoft(this.swPartNumberToDecide.id).subscribe({
        next: () => {
          this.fetchGroupedSwPartNumbers();
          this.selectNextSwPartNumber();
          this.swPartNumberToDecide = undefined;
        },
        error: (error) => {
          console.error(errorToString(error));
        },
      });
    }
  }

  public async handleDownload(swPartNumber: SwPartNumber): Promise<void> {
    const id = swPartNumber.id ?? '';
    try {
      const blob = await firstValueFrom(this.swPartNumberApiService.exportDefaultLanguageData(id));
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      CommonUtils.downloadFile(
        blob,
        `${swPartNumber.friendlyNumber || 'N_A'}-terminal-${TranslationService.toLanguageCode(TranslationService.defaultLanguageCode())}-${timestamp}.zip`
      );

      this.handleNewSelection(id);
    } catch (error) {
      console.error('Error downloading export:', errorToString(error));
    }
  }

  public async handleTrDocDownload(swPartNumber: SwPartNumber): Promise<void> {
    const id = swPartNumber.id ?? '';
    try {
      const blob = await firstValueFrom(this.swPartNumberApiService.exportTrDocData(id));
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      CommonUtils.downloadFile(
        blob,
        `${swPartNumber.friendlyNumber || 'N_A'}-documentation-${TranslationService.defaultLanguageCode()}-${timestamp}.xml`
      );

      this.handleNewSelection(id);
    } catch (error) {
      console.error('Error downloading export:', errorToString(error));
    }
  }

  public handleResetDocumentationExportedClicked(swPartNumber: SwPartNumber): void {
    const id = swPartNumber.id ?? '';
    this.swPartNumberApiService.resetDocumentationExportedForTrFlag(id).subscribe({
      next: () => {
        this.toast.success('Reset complete');
      },
      error: () => {
        console.error('Error resetting documentation exported flag');
      },
    });
  }

  public sendServiceBusEvents(swPartNumber: SwPartNumber): void {
    const id = swPartNumber.id ?? '';
    this.swPartNumberApiService.publishSyncEvents(id).subscribe({
      next: () => {
        this.toast.success('Published data synchronisation events (DTC, Error Message)');
      },
      error: () => {
        console.error('Error publishing data synchronisation events');
      },
    });
  }

  public handleResetOnTerminalExportedClicked(swPartNumber: SwPartNumber): void {
    const id = swPartNumber.id ?? '';
    this.swPartNumberApiService.resetOnTerminalExportedForTrFlag(id).subscribe({
      next: () => {
        this.toast.success('Reset complete');
      },
      error: () => {
        console.error('Error resetting on-terminal exported flag');
      },
    });
  }

  public onUpdatedSwPartNumber(swPartNumber: SwPartNumberUpdateConfirmation): void {
    this.fetchGroupedSwPartNumbers();
    this.selectedItem = swPartNumber;
    this.selectedTableElement.setSelectedElementId(swPartNumber.id ?? '', -1);
    this.fetchAggregatedTranslationStates(swPartNumber.id);
    this.configureDetailsView(this.selectedItem!);
    this.clearUnsavedData();
  }

  public async handleImport(upload: {
    swPartNumber: SwPartNumber;
    xmlFiles: File[];
    importType: ImportType;
  }): Promise<void> {
    try {
      let report: ImportReport | undefined = undefined;
      switch (upload.importType) {
        case ImportType.ImportTerminalMainLanguage:
          await firstValueFrom(
            this.swPartNumberApiService.importMainLanguageData(upload.swPartNumber.id!, upload.xmlFiles)
          );
          break;
        case ImportType.ImportTerminalTranslation:
          await firstValueFrom(
            this.swPartNumberApiService.importTranslatedLanguageData(upload.swPartNumber.id!, upload.xmlFiles)
          );
          break;
        case ImportType.ImportTrDocumentation:
          report = await firstValueFrom(
            this.swPartNumberApiService.importTrDocData(upload.swPartNumber.id!, upload.xmlFiles)
          );
          break;
        default:
          throw 'Unhandled import type detected';
      }

      if (report) {
        this.report = { swPartNumberId: upload.swPartNumber.id!, importReport: report };
        const blob = await firstValueFrom(this.serviceApiService.getImportLogfile(report.importId));
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        CommonUtils.downloadFile(blob, `${upload.swPartNumber.number}-import-log-${timestamp}.txt`);
        this.toastr.success('Import successful. Check the log file for details.');
      } else {
        this.toastr.success('Import started successfully. You will get a notification on completion.');
      }

      this.handleNewSelection(upload.swPartNumber.id!);
    } catch (error) {
      console.error('Error importing XML:', errorToString(error));
    }
  }

  public async handleNwNumberErrorMemorySelection(nwNumberErrorMemoryId: string): Promise<void> {
    this.nwNumberErrorMemory = await this.fetchNwNumberErrorMemory(nwNumberErrorMemoryId);
    this.configureSidebarForNwNumberErrorMemory();
    this.sideBarTitle = this.nwNumberErrorMemory?.number ?? '';
    this.sidebarOpen = true;
  }

  protected override handleNewSelection(id: string): void {
    this.configureSidebarForSwPartNumber();
    this.sidebarOpen = true;

    this.swPartNumberApiService.getSwPartNumber(id, true).subscribe({
      next: (swPartNumber: SwPartNumber) => {
        this.selectedItem = swPartNumber;
        this.selectedTableElement.setSelectedElementId(swPartNumber.id ?? '', -1);
        this.configureDetailsView(swPartNumber);
        this.sideBarTitle = `SW Part Number ${swPartNumber.displayId}`;
        this.fetchAggregatedTranslationStates(swPartNumber.id);
      },
      error: (error) => {
        console.error('Error fetching SW part number:', errorToString(error));
      },
    });
  }

  protected override getFriendlyItemName(item?: SwPartNumber | undefined): string {
    return item?.displayId ? item.displayId : 'SW Part Number';
  }

  protected override configureDetailsView(item: SwPartNumber): void {
    const textAreas: Textarea[] = [
      { sequence: 1, label: 'Major release', value: item.majorRelease ?? 'N/A', isTranslatable: false },
      {
        sequence: 2,
        label: 'Minor release',
        value: CommonUtils.maskSwPartNumberMinorRelease(item.minorRelease) ?? 'N/A',
        isTranslatable: false,
      },
      {
        sequence: 3,
        label: 'Created at',
        value: item.createdOn ? (this.datePipe.transform(item.createdOn, 'dd.MM.yyyy HH:mm') ?? 'N/A') : 'N/A',
        isTranslatable: false,
      },
      {
        sequence: 4,
        label: 'Created by',
        value: item.createdBy ?? 'N/A',
        isTranslatable: false,
      },
    ];

    if (item.state == SwPartNumberState.Released) {
      textAreas.push({
        sequence: 5,
        label: 'Released at',
        value: item.releasedOn ? (this.datePipe.transform(item.releasedOn, 'dd.MM.yyyy HH:mm') ?? 'N/A') : 'N/A',
        isTranslatable: false,
      });
      textAreas.push({
        sequence: 6,
        label: 'Released by',
        value: item.releasedBy ?? 'N/A',
        isTranslatable: false,
      });
    }

    const approvalStats = item.swPartNumberApprovalStats
      ? [
          {
            sequence: 8,
            approvalStatus: item.swPartNumberApprovalStats,
          } as ApprovalStatus,
        ]
      : undefined;

    const trReviewStatistics = item.trReviewStats
      ? [
          {
            sequence: 9,
            trReviewStatistics: item.trReviewStats,
          } as TrReviewStatistics,
        ]
      : undefined;

    const separators = item.swPartNumberApprovalStats ? [7, 10] : [];

    this.swPartNumberDetailConfiguration = {
      title: item.displayId + CommonUtils.swPartNumberStateToSymbol(item.state),
      textAreas: textAreas,
      displayLinkedDtcs: false,
      internalNotes: item.internalNotes,
      separators: separators,
      approvalStats: approvalStats,
      trReviewStats: trReviewStatistics,
    };
  }

  private fetchGroupedSwPartNumbers(): void {
    this.swPartNumberApiService.getGroupedSwPartNumbers().subscribe({
      next: (swPartNumbers: GroupedSwPartNumber[]) => {
        this.groupedSwPartNumber = swPartNumbers;
      },
      error: (error) => {
        console.error('Error fetching SW part numbers:', errorToString(error));
      },
    });
  }

  private createNewBranch(swPartNumber?: SwPartNumber): void {
    if (!swPartNumber) {
      return;
    }

    const createNewBranch: CreateMajorBranch = {
      swPartNumberId: swPartNumber.id,
    };

    this.swPartNumberApiService.createNewBranch(createNewBranch).subscribe({
      next: (createdSwPartNumber) => {
        this.reloadSwPartNumberList(createdSwPartNumber);
      },
      error: (error) => {
        console.error(errorToString(error));
      },
    });
  }

  private fetchAggregatedTranslationStates(swPartNumberId?: string): void {
    if (!swPartNumberId) {
      this.aggregatedLanguageTranslationStatesDocumentation = [];
      this.aggregatedLanguageTranslationStatesTerminal = [];
      return;
    }

    this.swPartNumberApiService.getAggregatedTranslationStates(swPartNumberId).subscribe({
      next: (data: AggreatedLanguageTranslationState) => {
        this.aggregatedLanguageTranslationStatesTerminal = data.translationStatesTerminal;
        this.aggregatedLanguageTranslationStatesDocumentation = data.translationStatesDocumentation;
      },
      error: (error) => {
        console.error('Error fetching Aggregated Translation States:', errorToString(error));
      },
    });
  }

  private async createSnapshot(
    sourceSwPartNumberId: string,
    targetNumber: string,
    minorVersion: string
  ): Promise<void> {
    const params = {
      number: targetNumber,
    } as SwPartNumberQuery;
    const existingSnapshots = await firstValueFrom(this.swPartNumberApiService.searchSwPartNumbers(params));
    if (existingSnapshots.totalCount > 0) {
      this.snapshotSourceIdToDecide = sourceSwPartNumberId;
      this.snapshotMinorVersionToDecide = minorVersion;
      this.updateSnapshotModal.openModal();
    } else {
      this.executeCreateSnapshot(sourceSwPartNumberId, minorVersion);
    }
  }

  private executeCreateSnapshot(sourceSwPartNumberId: string, minorVersion: string): void {
    const createSnapshot: CreateSnapshot = {
      swPartNumberId: sourceSwPartNumberId,
      minorVersion: minorVersion,
    };

    this.swPartNumberApiService.createSnapshot(createSnapshot).subscribe({
      next: (createdSwPartNumber) => {
        this.reloadSwPartNumberList(createdSwPartNumber);
      },
      error: (error) => {
        console.error(errorToString(error));
      },
    });
  }

  private reloadSwPartNumberList(newSwPartNumber: SwPartNumber): void {
    this.swPartNumberService.setSwPartNumber(newSwPartNumber);
    this.fetchGroupedSwPartNumbers();
    this.swPartNumberToDecide = undefined;
  }

  private selectNextSwPartNumber(): void {
    if (this.swPartNumberService.getSwPartNumberId() === this.swPartNumberToDecide?.id) {
      const nextSwPartNumber = this.groupedSwPartNumber?.[0]?.swPartNumbers?.[0];
      if (nextSwPartNumber) {
        this.swPartNumberService.setSwPartNumber(nextSwPartNumber);
      } else {
        console.warn('No next SW part number available after deletion.');
      }
    }
  }

  private buildHelpParagraphs(): void {
    this.paragraphs = [
      {
        title: 'Actions',
        header: this.sanitizer.bypassSecurityTrustHtml(ICONS_RAW.nwNumberButtonIcon),
        content:
          'Clicking on the NW number opens the corresponding NW number error memory in the sidebar. Clicking outside the button opens the SW Part Number details in the sidebar.',
      },
      {
        header: this.sanitizer.bypassSecurityTrustHtml(ICONS_RAW.deleteIcon),
        content: 'Soft delete a SW Part Number. The SW Part Number can be restored later if needed.',
      },
      {
        header: this.sanitizer.bypassSecurityTrustHtml(ICONS_RAW.addIcon),
        content: 'Create or update a snapshot',
      },
      {
        header: this.sanitizer.bypassSecurityTrustHtml(ICONS_RAW.branchIcon),
        content: 'Prepare a new major release',
      },
      {
        header: this.sanitizer.bypassSecurityTrustHtml(ICONS_RAW.downloadIcon),
        content: 'Opens a popover to download different types of export files for the SW Part Number',
      },
      {
        header: this.sanitizer.bypassSecurityTrustHtml(ICONS_RAW.importIcon),
        content: 'Opens a popover to import data to the SW Part Number',
      },
      {
        header: this.sanitizer.bypassSecurityTrustHtml(ICONS_RAW.noteIcon),
        content: 'View notes associated with the SW Part Number',
      },
      {
        header: this.sanitizer.bypassSecurityTrustHtml(ICONS_RAW.rocketIcon),
        content: 'Release a snapshot to make it a major release',
      },
      {
        header: this.sanitizer.bypassSecurityTrustHtml(ICONS_RAW.refreshIcon),
        content: 'Update the snapshot with the latest data from the parent SW Part Number',
      },
      {
        header: '🔒',
        content: 'The SW Part Number has the state SNAPSHOT and is read-only',
      },
      {
        header: '🔧',
        content: 'The SW Part Number has the state DEV and can be edited',
      },
    ];
  }

  private configureSidebarForNwNumberErrorMemory(): void {
    this.sidebarConfiguration.translationsSettingsAreaEnabled = true;
    this.sidebarConfiguration.auditLogAreaEnabled = false;
    this.sidebarConfiguration.editAreaEnabled = false;
    this.sidebarConfiguration.translationsAreaEnabled = false;
    this.sidebarConfiguration.detailedTranslationsAreaEnabled = false;
    this.sidebarConfiguration.summaryAreaEnabled = false;
    this.sidebarConfiguration.initArea = SIDEBAR_AREA.languageConfiguration;
  }

  private configureSidebarForSwPartNumber(): void {
    this.sidebarConfiguration.translationsSettingsAreaEnabled = false;
    this.sidebarConfiguration.auditLogAreaEnabled = true;
    this.sidebarConfiguration.editAreaEnabled = true;
    this.sidebarConfiguration.translationsAreaEnabled = false;
    this.sidebarConfiguration.detailedTranslationsAreaEnabled = true;
    this.sidebarConfiguration.summaryAreaEnabled = true;
    this.sidebarConfiguration.initArea = SIDEBAR_AREA.summary;
  }

  private async fetchNwNumberErrorMemory(id: string): Promise<NwNumberErrorMemory> {
    const nwNumberErrorMemory = await firstValueFrom(this.nwNumberErrorMemoryApi.getNwNumberErrorMemoryById(id));
    return nwNumberErrorMemory;
  }
}
